<template>
    <div class="flex flex-col dark:text-white">
        <h3 class="my-2 text-base font-semibold text-gray-700 dark:text-gray-200">
            Assinatura
        </h3>
        <div class="border border-black/5 dark:border-white/10 min-w-0 w-full rounded-xl overflow-hidden bg-white dark:bg-white/[0.02] mb-4 shadow-xs p-5">
            <div v-if="!loading" class="grid grid-cols-3 gap-3">
                <div class="flex flex-col">
                    <span
                        class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500"
                        >Número</span
                    >
                    <span
                        class="text-gray-800 dark:text-gray-200"
                        >#{{ subscription.id }}</span
                    >
                </div>
                <div class="flex flex-col">
                    <span
                        class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500"
                        >Periodicidade</span
                    >
                    <span
                        class="text-gray-800 dark:text-gray-200"
                        >{{ formatInterval(subscription.interval) }}</span
                    >
                </div>
                <div class="flex flex-col" v-if="subscription.next_charge">
                    <span
                        class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500"
                        >Data da Próxima Cobrança</span
                    >
                    <span
                        class="text-gray-800 dark:text-gray-200"
                        >
                        {{ formatDateWithoutTime(subscription.next_charge) }}
                        -
                        <button
                            class="text-indigo-500 hover:underline"
                            @click.prevent="cancel()"
                        >
                            Cancelar
                        </button>
                        -
                        <button
                            class="text-red-500 hover:underline"
                            @click.prevent="showRemoveModal = true"
                        >
                            Remover
                        </button>
                        <template v-if="canPostponeCharge">
                            -
                            <button
                                class="text-emerald-600 hover:underline"
                                @click.prevent="reactivate(true)"
                            >
                                Adiar Cobrança
                            </button>
                        </template>
                    </span>
                </div>
                <div class="flex flex-col" v-if="subscription.canceled_at">
                    <span
                        class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500"
                        >Data do Cancelamento</span
                    >
                    <span
                        class="text-gray-800 dark:text-gray-200"
                        >{{ formatDateWithoutTime(subscription.canceled_at) }}
                        -
                        <button
                            class="text-emerald-600 hover:underline"
                            @click.prevent="reactivate()"
                        >
                            Reativar
                        </button>
                        -
                        <button
                            class="text-red-500 hover:underline"
                            @click.prevent="showRemoveModal = true"
                        >
                            Remover
                        </button>
                        <br/>
                        <small class="text-gray-800 dark:text-gray-200">
                            ({{ subscription.canceled_by ? "Cancelada por " + subscription.canceled_by_name : 'Cancelada automaticamente' }})
                        </small>
                    </span>
                </div>
                <div class="flex flex-col">
                    <span
                        class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500"
                        >Quantidade de Cobranças a serem Realizadas</span
                    >
                    <span
                        class="text-gray-800 dark:text-gray-200"
                        >{{ subscription.max_charges ?? 'Renovação Automática' }}</span
                    >
                </div>
                <div class="flex flex-col">
                    <span
                        class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500"
                        >Quantidade de Cobranças Bem-sucedidas</span
                    >
                    <span
                        class="text-gray-800 dark:text-gray-200"
                        >{{ subscription.successful_charges }}</span
                    >
                </div>
                <div class="flex flex-col">
                    <span
                        class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500"
                        >Quantidade de Cobranças Mal-sucedidas</span
                    >
                    <span
                        class="text-gray-800 dark:text-gray-200"
                        >{{ subscription.failed_charges }}</span
                    >
                </div>
                <div class="flex flex-col">
                      <span
                        class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500"
                        >Assinatura Migrada</span
                    >

                    <info-col>
                        <CheckCircleIcon class="h-4 w-4 text-emerald-500" aria-hidden="true" v-if="subscription.legacy_id" />
                        <XCircleIcon class="h-4 w-4 text-gray-400 dark:text-gray-500" aria-hidden="true" v-if="!subscription.legacy_id" />
                    </info-col>
                </div>
                <div v-if="subscription.payment_method === 'pix_recurring'" class="flex flex-col">
                    <span
                        class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500"
                        >Metadados PIX Automático</span
                    >
                    <button
                        class="text-indigo-500 hover:underline text-left"
                        @click.prevent="loadPixRecurringMetadata()"
                    >
                        Visualizar Metadados
                    </button>
                </div>
                <div class="flex flex-col">
                    <span
                        class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500"
                        >Método de Pagamento</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">
                        {{ formatPaymentMethod(subscription.payment_method) }}
                    </span>
                </div>
            </div>
            <div v-else>
                <span class="text-gray-800 dark:text-gray-200">
                    Realizando solicitação...
                </span>
            </div>
        </div>

        <!-- Modal de confirmação para remoção -->
        <modal :open="showRemoveModal" @close="showRemoveModal = false">
            <div class="mb-4">
                <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-3">Confirmar Remoção</h3>
                <div class="text-left">
                    <p class="text-sm text-gray-500 dark:text-gray-300 mb-2">
                        Tem certeza que deseja remover definitivamente esta assinatura?
                    </p>
                    <p class="text-sm text-red-600 dark:text-red-400">
                        Esta ação não pode ser desfeita.
                    </p>
                </div>
            </div>

            <div class="mt-2 flex flex-col space-y-1">
                <button
                    @click="remove"
                    :disabled="loading"
                    class="w-full inline-flex justify-center items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:cursor-not-allowed disabled:bg-red-400"
                >
                    {{ loading ? 'Removendo...' : 'Remover' }}
                </button>
                <button
                    @click="showRemoveModal = false"
                    class="w-full inline-flex justify-center items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                    Cancelar
                </button>
            </div>
        </modal>

        <!-- Modal de Metadados PIX Automático -->
        <modal :open="showPixMetadataModal" @close="showPixMetadataModal = false" size="large">
            <div class="mb-4">
                <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-1">Metadados PIX Automático</h3>
                <p class="text-sm text-gray-500 dark:text-gray-400">Informações detalhadas sobre a autorização e configuração</p>
            </div>

            <div class="max-h-[70vh] overflow-y-auto">

                <!-- Loading State -->
                <div v-if="loadingPixMetadata" class="text-center py-12">
                    <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent"></div>
                    <p class="text-gray-500 dark:text-gray-300 mt-4">Carregando informações...</p>
                </div>

                <!-- Content -->
                <div v-else-if="pixMetadata" class="space-y-6">
                    <!-- Status Badge -->
                    <div class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <span class="text-sm font-medium text-gray-600 dark:text-gray-300">Status da Autorização</span>
                        <span :class="getStatusBadgeClass(pixMetadata.status)" class="px-3 py-1 rounded-full text-sm font-semibold">
                            {{ formatPixStatus(pixMetadata.status) }}
                        </span>
                    </div>

                    <!-- Grid de Informações Principais -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Data de Autorização -->
                        <div v-if="pixMetadata.authorization_date" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
                            <div class="flex items-start gap-3">
                                <div class="shrink-0 w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                                    <svg class="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                </div>
                                <div class="flex-1">
                                    <p class="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Data de Autorização</p>
                                    <p class="text-sm font-semibold text-gray-900 dark:text-white mt-1">{{ formatDate(pixMetadata.authorization_date) }}</p>
                                </div>
                            </div>
                        </div>

                        <!-- Data de Cancelamento -->
                        <div v-if="pixMetadata.cancelled_at" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
                            <div class="flex items-start gap-3">
                                <div class="shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
                                    <svg class="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                </div>
                                <div class="flex-1">
                                    <p class="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Data de Cancelamento</p>
                                    <p class="text-sm font-semibold text-gray-900 dark:text-white mt-1">{{ formatDate(pixMetadata.cancelled_at) }}</p>
                                </div>
                            </div>
                        </div>

                        <!-- Última Tentativa -->
                        <div v-if="pixMetadata.last_charge_attempt" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
                            <div class="flex items-start gap-3">
                                <div class="shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                                    <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                </div>
                                <div class="flex-1">
                                    <p class="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Última Tentativa</p>
                                    <p class="text-sm font-semibold text-gray-900 dark:text-white mt-1">{{ formatDate(pixMetadata.last_charge_attempt) }}</p>
                                </div>
                            </div>
                        </div>

                        <!-- Tentativas de Retry -->
                        <div v-if="pixMetadata.retry_attempts !== undefined" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
                            <div class="flex items-start gap-3">
                                <div class="shrink-0 w-10 h-10 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
                                    <svg class="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                                    </svg>
                                </div>
                                <div class="flex-1">
                                    <p class="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Tentativas de Retry</p>
                                    <p class="text-sm font-semibold text-gray-900 dark:text-white mt-1">
                                        {{ pixMetadata.retry_attempts }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Configurações -->
                    <div class="bg-linear-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-750 rounded-lg p-5 border border-gray-200 dark:border-gray-600">
                        <h4 class="text-sm font-bold text-gray-700 dark:text-gray-200 mb-4 flex items-center gap-2">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            </svg>
                            Configurações da Autorização
                        </h4>
                        <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div v-if="pixMetadata.frequency">
                                <p class="text-xs text-gray-500 dark:text-gray-400">Frequência</p>
                                <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatFrequency(pixMetadata.frequency) }}</p>
                            </div>
                            <div v-if="pixMetadata.pull_mode">
                                <p class="text-xs text-gray-500 dark:text-gray-400">Modo de Cobrança</p>
                                <p class="text-sm font-medium text-gray-900 dark:text-white capitalize">{{ formatPullMode(pixMetadata.pull_mode) }}</p>
                            </div>
                            <div v-if="pixMetadata.variable_amount !== undefined">
                                <p class="text-xs text-gray-500 dark:text-gray-400">Valor Variável</p>
                                <p class="text-sm font-medium text-gray-900 dark:text-white">{{ pixMetadata.variable_amount ? 'Sim' : 'Não' }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- IDs e Referências -->
                    <div class="space-y-3">
                        <h4 class="text-sm font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                            </svg>
                            Identificadores
                        </h4>

                        <div v-if="pixMetadata.starkbank_subscription_id" class="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border-l-4 border-indigo-500">
                            <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">StarkBank Subscription ID</p>
                            <p class="text-sm font-mono text-gray-900 dark:text-white break-all">{{ pixMetadata.starkbank_subscription_id }}</p>
                        </div>

                        <div v-if="pixMetadata.external_id" class="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border-l-4 border-purple-500">
                            <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">External ID</p>
                            <p class="text-sm font-mono text-gray-900 dark:text-white break-all">{{ pixMetadata.external_id }}</p>
                        </div>

                        <div v-if="gatewayAuthorizationCode" class="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border-l-4 border-green-500">
                            <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">{{ gatewayAuthorizationLabel }}</p>
                            <p class="text-sm font-mono text-gray-900 dark:text-white break-all">{{ gatewayAuthorizationCode }}</p>
                        </div>

                        <div v-if="pixMetadata.bacen_id" class="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border-l-4 border-yellow-500">
                            <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">BACEN ID</p>
                            <p class="text-sm font-mono text-gray-900 dark:text-white break-all">{{ pixMetadata.bacen_id }}</p>
                        </div>

                        <div v-if="pixMetadata.last_invoice_id" class="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border-l-4 border-blue-500">
                            <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Último Invoice ID</p>
                            <p class="text-sm font-mono text-gray-900 dark:text-white break-all">{{ pixMetadata.last_invoice_id }}</p>
                        </div>
                    </div>

                    <!-- Metadata Adicional -->
                    <div v-if="pixMetadata.metadata && Object.keys(pixMetadata.metadata).length > 0" class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                        <h4 class="text-sm font-bold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"></path>
                            </svg>
                            Metadata
                        </h4>
                        <div class="bg-white dark:bg-gray-800 rounded p-3 max-h-64 overflow-auto">
                            <pre class="text-xs text-gray-600 dark:text-gray-300 font-mono">{{ JSON.stringify(pixMetadata.metadata, null, 2) }}</pre>
                        </div>
                    </div>
                </div>

                <!-- Error State -->
                <div v-else class="text-center py-12">
                    <div class="w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg class="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                    </div>
                    <p class="text-sm text-red-600 dark:text-red-400 font-medium">{{ pixMetadataError || 'Erro ao carregar metadados' }}</p>
                </div>
            </div>

            <!-- Footer Actions -->
            <div class="mt-6">
                <button
                    @click="showPixMetadataModal = false"
                    class="w-full inline-flex justify-center items-center rounded-lg px-5 py-2 text-sm font-medium text-white bg-indigo-500 hover:bg-indigo-700 shadow"
                >
                    Fechar
                </button>
            </div>
        </modal>
    </div>
</template>

<script>
import { CheckCircleIcon, XCircleIcon } from "@heroicons/vue/24/outline";
import HelperMixin from "../../mixins/HelperMixin.vue";
import Modal from "../../components/layouts/Modal.vue";

export default {
    components: {
        Modal,
        CheckCircleIcon,
        XCircleIcon,
    },
    props: ["subscription"],
    emits: ["updated"],
    mixins: [HelperMixin],
    data() {
        return {
            loading: false,
            next_page_url: false,
            commissions: [],
            filters: {
                text: "",
                status: [],
                payment_method: [],
            },
            showRemoveModal: false,
            showPixMetadataModal: false,
            loadingPixMetadata: false,
            pixMetadata: null,
            pixMetadataError: null,
        };
    },
    computed: {
        gatewayAuthorizationCode() {
            if (!this.pixMetadata) {
                return null;
            }

            return this.pixMetadata.gateway === 'autorizou'
                ? this.pixMetadata.charge_code
                : this.pixMetadata.authorization_id;
        },
        gatewayAuthorizationLabel() {
            return this.pixMetadata?.gateway === 'autorizou'
                ? 'Charge Code (Autorizou)'
                : 'Authorization ID (StarkBank)';
        },
        canPostponeCharge() {
            const raw = this.subscription?.next_charge;

            if (!raw) {
                return false;
            }

            let nextCharge;

            if (raw.includes('/')) {
                const [day, month, year] = raw.split(' ')[0].split('/');
                nextCharge = new Date(Number(year), Number(month) - 1, Number(day));
            } else {
                nextCharge = new Date(raw);
                nextCharge.setHours(0, 0, 0, 0);
            }

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            return nextCharge.getTime() === today.getTime();
        },
    },
    methods: {
        formatInterval(interval) {
            switch (interval) {
                case 0:
                    return "Semanal";
                case 1:
                    return "Mensal";
                case 2:
                    return "Bimestral";
                case 3:
                    return "Trimestral";
                case 6:
                    return "Semestral";
                case 12:
                    return "Anual";
                default:
                    return "-";
            }
        },
        cancel() {
            if (confirm("Tem certeza que deseja cancelar essa assinatura?")) {
                this.loading = true;

                axios.post('/api/subscription/' + this.subscription.id + '/cancel', {
                    permissions: this.permissions,
                }).then(({data}) => {
                    this.$emit('updated');

                    setTimeout(() => {
                        this.$notify({
                            group: "success",
                            title: "Success",
                            text: data?.message || "Solicitação realizada com sucesso!"
                        }, 4000);

                        this.loading = false;
                    }, 1000);
                }).catch(({response}) => {
                    this.$notify({
                        group: "error",
                        title: "Error",
                        text: response?.data?.message || response?.data?.errors[0] || "Não foi possível concluir essa solicitação."
                    }, 4000);

                    this.loading = false;
                });
            }
        },
        reactivate(postponeCharge = false) {
            const question = postponeCharge
                ? "Adiar a cobrança para o próximo dia útil? A assinatura não será cobrada agora."
                : "Reativar a assinatura sem cobrança imediata? A próxima cobrança será agendada para o próximo dia útil.";

            if (confirm(question)) {
                this.loading = true;

                axios.post('/api/subscription/' + this.subscription.id + '/reactivate').then(({data}) => {
                    this.$emit('updated');

                    setTimeout(() => {
                        this.$notify({
                            group: "success",
                            title: "Success",
                            text: data?.message || "Assinatura reativada com sucesso!"
                        }, 4000);

                        this.loading = false;
                    }, 1000);
                }).catch(({response}) => {
                    this.$notify({
                        group: "error",
                        title: "Error",
                        text: response?.data?.message || response?.data?.errors?.[0] || "Não foi possível concluir essa solicitação."
                    }, 4000);

                    this.loading = false;
                });
            }
        },
        remove() {
            this.loading = true;
            this.showRemoveModal = false;

            axios.delete('/api/subscription/' + this.subscription.id, {
                permissions: this.permissions,
            }).then(({data}) => {
                this.$emit('updated');

                setTimeout(() => {
                    this.$notify({
                        group: "success",
                        title: "Success",
                        text: data?.message || "Assinatura removida com sucesso!"
                    }, 4000);

                    this.loading = false;
                }, 1000);
            }).catch(({response}) => {
                this.$notify({
                    group: "error",
                    title: "Error",
                    text: response?.data?.message || response?.data?.errors[0] || "Não foi possível concluir essa solicitação."
                }, 4000);

                this.loading = false;
            });
        },
        loadPixRecurringMetadata() {
            this.showPixMetadataModal = true;
            this.loadingPixMetadata = true;
            this.pixMetadata = null;
            this.pixMetadataError = null;

            axios.get('/api/subscription/' + this.subscription.id + '/pix-recurring-metadata')
                .then(({data}) => {
                    this.pixMetadata = data;
                    this.loadingPixMetadata = false;
                })
                .catch(({response}) => {
                    this.pixMetadataError = response?.data?.error || 'Erro ao carregar metadados';
                    this.loadingPixMetadata = false;

                    this.$notify({
                        group: "error",
                        title: "Error",
                        text: this.pixMetadataError
                    }, 4000);
                });
        },
        formatPixStatus(status) {
            const statusMap = {
                'pending': 'Pendente',
                'authorized': 'Autorizado',
                'active': 'Ativo',
                'cancelled': 'Cancelado',
                'expired': 'Expirado',
                'failed': 'Falhou',
                'paused': 'Pausado'
            };
            return statusMap[status] || status;
        },
        getStatusBadgeClass(status) {
            const statusClasses = {
                'pending': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
                'authorized': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
                'active': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                'cancelled': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                'expired': 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200',
                'failed': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                'paused': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
            };
            return statusClasses[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200';
        },
        formatFrequency(frequency) {
            const frequencyMap = {
                'weekly': 'Semanal',
                'biweekly': 'Quinzenal',
                'monthly': 'Mensal',
                'quarterly': 'Trimestral',
                'biannual': 'Semestral',
                'annual': 'Anual'
            };
            return frequencyMap[frequency] || frequency;
        },
        formatPullMode(pullMode) {
            const pullModeMap = {
                'manual': 'Manual',
                'automatic': 'Automático'
            };
            return pullModeMap[pullMode] || pullMode;
        },
        formatCurrency(value) {
            if (!value) return '0,00';
            return parseFloat(value).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        },
        formatPaymentMethod(method) {
            const methodMap = {
                'credit_card': 'Cartão de Crédito',
                'debit_card': 'Cartão de Débito',
                'bank_slip': 'Boleto',
                'pix': 'PIX',
                'pix_recurring': 'PIX Automático',
                'paypal': 'PayPal'
            };
            return methodMap[method] || method;
        },
    }
};
</script>
